import { useRef, useState } from "react";
import { Loader2, ImageIcon, Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { id as localeId } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// ETD stored as ISO 'yyyy-MM-dd' (backend/overdue unchanged), shown as DD/MM/YYYY.
function parseLocalISO(s) {
  const [y, m, d] = String(s).split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}
function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function ETDDatePicker({ value, onChange, testId, className = "" }) {
  const [open, setOpen] = useState(false);
  const selected = value ? parseLocalISO(value) : undefined;
  const display = selected ? format(selected, "dd/MM/yyyy") : "";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`neon-input h-9 w-full rounded-lg px-3 flex items-center justify-between text-sm ${className}`}
        >
          <span className={display ? "text-white" : "text-[var(--text-muted)]"}>{display || "Pilih tanggal"}</span>
          <CalendarIcon size={15} className="text-[var(--neon-cyan)] shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={testId ? `${testId}-calendar` : undefined}>
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected || startOfToday()}
          locale={localeId}
          onSelect={(d) => { onChange(d ? toISO(d) : ""); setOpen(false); }}
          disabled={{ before: startOfToday() }}
          fromDate={startOfToday()}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold tracking-tight text-white glow-text">{title}</h1>
        {subtitle && <p className="text-gray-500 text-sm mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

export function StatCard({ label, value, sub, accent, icon: Icon, testId, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="neon-card text-left p-5 w-full group cursor-pointer"
    >
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center mb-3 border ${accent ? "border-[rgba(255,120,150,0.5)] bg-[rgba(255,60,90,0.12)]" : "border-[rgba(0,200,255,0.4)] bg-[rgba(0,180,255,0.08)]"}`}>
        {Icon && <Icon size={20} className={accent ? "text-[#FF8CA3]" : "icon-cyan"} strokeWidth={2} />}
      </div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500">{label}</div>
      <div className={`mt-1 font-head text-4xl font-extrabold leading-none ${accent ? "text-[#FF8CA3]" : "text-white"} group-hover:glow-text`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1.5">{sub}</div>}
      <div className="kpi-underline" />
    </button>
  );
}

export function Card({ children, className = "", ...rest }) {
  return <div className={`neon-card ${className}`} {...rest}>{children}</div>;
}

export function EmptyState({ title, subtitle, icon: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
      <div className="h-14 w-14 rounded-full flex items-center justify-center mb-3 border border-[rgba(0,200,255,0.35)] bg-[rgba(0,180,255,0.06)]">
        {Icon ? <Icon size={26} className="icon-cyan" /> : <ImageIcon size={26} className="icon-cyan" />}
      </div>
      <div className="font-head font-semibold text-white">{title}</div>
      {subtitle && <div className="text-sm text-gray-500 mt-1 max-w-xs">{subtitle}</div>}
    </div>
  );
}

export function Spinner({ label }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-gray-400">
      <Loader2 className="animate-spin icon-cyan" size={20} /> {label || "Memuat…"}
    </div>
  );
}

export function JobIdentity({ job, size = "md" }) {
  const big = size === "lg";
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="job-identity">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-400">NOPOL</div>
        <div className={`nopol-lg font-bold text-white ${big ? "text-2xl sm:text-4xl glow-text" : "text-lg"}`}>{job.nopol || "-"}</div>
      </div>
      <div className="h-8 w-px bg-[rgba(0,200,255,0.25)]" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-400">NO CLAIM</div>
        <div className={`font-mono-data font-semibold text-gray-700 ${big ? "text-base sm:text-lg" : "text-sm"}`}>{job.claim_number || "—"}</div>
      </div>
      <div className="h-8 w-px bg-[rgba(0,200,255,0.25)]" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-gray-400">NO SPK</div>
        <div className={`font-mono-data font-semibold text-gray-700 ${big ? "text-base sm:text-lg" : "text-sm"}`}>{job.spk_number || "—"}</div>
      </div>
    </div>
  );
}

// generic file-to-server uploader trigger button
export function useFileUpload() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const open = () => inputRef.current?.click();
  const El = (
    <input
      ref={inputRef}
      type="file"
      accept=".pdf,.jpg,.jpeg,.png,.webp,image/*"
      className="hidden"
      onChange={(e) => setFile(e.target.files?.[0] || null)}
    />
  );
  return { inputRef, file, setFile, open, El };
}
